import { Link } from "react-router-dom";
import { Check, Beaker, ShieldCheck, Droplets, Layers, Flame, Factory, ArrowRight, Phone } from "lucide-react";
import PageHero from "../components/PageHero";
import PinkwaterLabel from "../components/PinkwaterLabel";
import PinkwaterCalculator from "../components/PinkwaterCalculator";
import PinkwaterDeck from "../components/PinkwaterDeck";
import Reveal from "../components/Reveal";
import FlyerDownloadButton from "../components/FlyerDownloadButton";
import { pinkwaterContent, company } from "../mock";

const stepIcons = [Layers, Droplets];
const fieldIconMap = { Factory, Droplets, Layers };

export default function Pinkwater() {
  const c = pinkwaterContent;
  return (
    <div>
      <PageHero kicker={c.kicker} title={c.title} image={c.headerImage} />

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <div className="flex justify-center mb-6">
            <PinkwaterLabel size="lg" />
          </div>
          <h2 className="font-heading text-2xl md:text-3xl text-[#1a2540] leading-snug mb-6">
            {c.headline}
          </h2>
          <div className="w-14 h-[3px] bg-[#d81b76] mx-auto mb-10" />
          {c.overview.map((p, i) => (
            <p key={i} className="text-[#6a6a6a] leading-relaxed mb-6">
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-br from-[#a01259] to-[#d81b76]">
        <div className="mx-auto max-w-[1000px] px-6 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {c.stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-light text-white mb-3">{s.value}</p>
              <p className="text-white/85 text-sm leading-relaxed max-w-[240px] mx-auto">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 msv-dot-bg">
        <div className="mx-auto max-w-[1100px] px-6">
          <Reveal className="text-center mb-14">
            <p className="msv-eyebrow mb-4">How It Works</p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540] mb-5">
              Two-Way Hydrocarbon Mitigation
            </h2>
            <p className="text-[#6a6a6a] leading-relaxed max-w-2xl mx-auto">{c.howIntro}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {c.howSteps.map((step, i) => {
              const Icon = stepIcons[i % stepIcons.length];
              return (
                <Reveal key={step.number} delay={i * 100}>
                <div className="group msv-card h-full p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="msv-icon w-12 h-12 shrink-0">
                      <Icon size={22} />
                    </div>
                    <span className="font-heading text-4xl font-light text-[#f0d3e2]">{step.number}</span>
                  </div>
                  <h3 className="font-heading text-lg uppercase tracking-[0.04em] text-[#1a2540] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#6a6a6a] leading-relaxed">{step.text}</p>
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fuel spill highlight */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[1000px] px-6">
          <Reveal>
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1a2540] to-[#0f2c4a] p-10 md:p-14 shadow-[0_25px_55px_-25px_rgba(15,41,74,0.6)]">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#d81b76]/25 blur-3xl" />
            <div className="relative flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#d81b76]/20 flex items-center justify-center">
                <Flame size={22} className="text-[#ff7ab5]" />
              </div>
              <h2 className="font-heading text-2xl uppercase tracking-[0.05em] text-white">
                {c.fuelSpill.title}
              </h2>
            </div>
            <p className="relative text-gray-200 leading-relaxed max-w-3xl">{c.fuelSpill.text}</p>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Interactive volume estimator */}
      <PinkwaterCalculator />

      {/* Applications & Benefits */}
      <section className="py-20 msv-dot-bg">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
            <div className="msv-card h-full p-8 md:p-10">
              <div className="flex items-center gap-2.5 mb-6">
                <Beaker size={20} className="text-[#1b75bc]" />
                <h3 className="font-heading text-lg uppercase tracking-[0.08em] text-[#1a2540]">
                  Applications Include
                </h3>
              </div>
              <ul className="space-y-3">
                {c.applications.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check size={16} className="text-[#1b75bc] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#4a4a4a] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            </Reveal>
            <Reveal delay={100}>
            <div className="msv-card h-full p-8 md:p-10">
              <div className="flex items-center gap-2.5 mb-6">
                <ShieldCheck size={20} className="text-[#1b75bc]" />
                <h3 className="font-heading text-lg uppercase tracking-[0.08em] text-[#1a2540]">
                  Key Benefits
                </h3>
              </div>
              <ul className="space-y-3">
                {c.benefits.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check size={16} className="text-[#1b75bc] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#4a4a4a] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            </Reveal>
          </div>

          <div className="mt-8 bg-white rounded-xl border-l-4 border-[#d81b76] p-6 md:p-8 shadow-[0_10px_30px_-18px_rgba(15,41,74,0.25)]">
            <p className="text-sm text-[#4a4a4a] leading-relaxed italic">{c.closing}</p>
          </div>
        </div>
      </section>

      {/* In the Field applications */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[1100px] px-6">
          <Reveal className="text-center mb-14">
            <p className="msv-eyebrow mb-4">In The Field</p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              Oil &amp; Gas Applications
            </h2>
            <p className="text-[#6a6a6a] leading-relaxed mt-5 max-w-2xl mx-auto">
              Proven across offshore platforms and tank-cleaning operations for vapor suppression,
              degassing, and hydrocarbon recovery.
            </p>
          </Reveal>
          <div className="space-y-6">
            {c.fieldApplications.map((app, i) => {
              const Icon = fieldIconMap[app.icon] || Droplets;
              return (
                <Reveal key={app.title} delay={i * 90}>
                <div className="group msv-card p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="md:w-1/3">
                      <div className="msv-icon w-14 h-14 mb-4">
                        <Icon size={26} />
                      </div>
                      <h3 className="font-heading text-lg uppercase tracking-[0.04em] text-[#1a2540] mb-2 leading-tight">
                        {app.title}
                      </h3>
                      <p className="text-sm text-[#6a6a6a] leading-relaxed">{app.summary}</p>
                    </div>
                    <ul className="md:w-2/3 space-y-3 md:border-l md:border-gray-100 md:pl-8">
                      {app.points.map((point) => (
                        <li key={point} className="flex items-start gap-3">
                          <Check size={16} className="text-[#d81b76] mt-0.5 shrink-0" />
                          <span className="text-sm text-[#4a4a4a] leading-relaxed">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pinkwater presentation deck */}
      <PinkwaterDeck />

      {/* CTA */}
      <section className="py-20 bg-[#1a2540]">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <h2 className="font-heading text-3xl uppercase tracking-[0.08em] text-white mb-5">
            Interested in Pinkwater®?
          </h2>
          <p className="text-gray-300 leading-relaxed mb-8">
            Contact Market Street Ventures for product information, technical support, and orders.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.2em] text-sm text-white bg-[#d81b76] px-10 py-4 hover:bg-[#b5155f] transition-colors duration-300"
            >
              Contact Us <ArrowRight size={16} />
            </Link>
            <FlyerDownloadButton product="pinkwater" accent="#d81b76" accentHover="#b5155f" />
            <a
              href={`tel:${company.phoneRaw}`}
              className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.2em] text-sm text-white/90 hover:text-white transition-colors"
            >
              <Phone size={16} /> {company.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
